import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { StatutExploitation, StatutMembre } from '@genviz/shared/enums';

import { useChangerStatutExploitation, useChangerStatutUtilisateur, useExploitation } from '../api/queries';
import type { AppareilExploitation, MembreBackOffice } from '../api/types';
import { Capacite } from '../auth/permissions';
import { usePeut } from '../auth/useSession';
import {
  BadgeConnectivite,
  BadgeEtatAppareil,
  BadgeStatutExploitation,
  BadgeStatutMembre,
  PastilleNombre,
} from '../components/Badges';
import { Bouton } from '../components/Boutons';
import { Carte, Definitions, Tuile } from '../components/Carte';
import { ZoneTexte } from '../components/Champs';
import { Chargement } from '../components/Chargement';
import { MessageErreur, texteErreur } from '../components/MessageErreur';
import { Modale } from '../components/Modale';
import { useNotifications } from '../components/Notifications';
import { Tableau, type Colonne } from '../components/Tableau';
import { LIBELLE_MODELE, LIBELLE_ROLE_UTILISATEUR, VALEUR_ABSENTE } from '../i18n/fr';
import { formaterDateHeure, formaterNombre, formaterRelatif, ouAbsent } from '../lib/format';

/**
 * Fiche d'une exploitation cliente.
 *
 * La suspension est traitée comme une opération de dernier recours et se comporte comme telle :
 * motif obligatoire, confirmation par saisie du nom, et rappel de ce qu'elle fait vraiment —
 * elle coupe l'accès des comptes, PAS l'ingestion. Continuer à collecter les trames d'un client
 * suspendu est délibéré : si le litige se règle, l'historique n'a pas de trou, et si l'appareil
 * est volé pendant la suspension, la trace existe.
 */
export function ExploitationPage(): JSX.Element {
  const { id = '' } = useParams();
  const naviguer = useNavigate();
  const notifications = useNotifications();
  const peutSuspendre = usePeut(Capacite.SUSPENDRE_COMPTE);

  const { data, error, isPending } = useExploitation(id);
  const statutUtilisateur = useChangerStatutUtilisateur(id);

  const [suspensionOuverte, setSuspensionOuverte] = useState(false);

  if (isPending) return <Chargement libelle="Chargement de l’exploitation…" />;
  if (error) return <MessageErreur erreur={error} />;
  if (!data) return <Chargement libelle="Chargement de l’exploitation…" />;

  const suspendue = data.statut === StatutExploitation.SUSPENDUE;

  const colonnesUtilisateurs: Array<Colonne<MembreBackOffice>> = [
    {
      cle: 'identite',
      entete: 'Utilisateur',
      rendu: (u) =>
        u.statut === StatutMembre.INVITATION_EN_ATTENTE ? (
          <span className="gv-secondaire">invitation non acceptée</span>
        ) : (
          `${u.prenom} ${u.nom}`
        ),
    },
    { cle: 'email', entete: 'E-mail', rendu: (u) => <span className="gv-mono gv-corps-s">{u.email}</span> },
    { cle: 'role', entete: 'Rôle', rendu: (u) => LIBELLE_ROLE_UTILISATEUR[u.role] },
    { cle: 'statut', entete: 'Statut', rendu: (u) => <BadgeStatutMembre statut={u.statut} /> },
    {
      cle: 'connexion',
      entete: 'Dernière connexion',
      rendu: (u) => (u.derniere_connexion_at ? formaterRelatif(u.derniere_connexion_at) : VALEUR_ABSENTE),
    },
    {
      cle: 'actions',
      entete: '',
      rendu: (u) => {
        if (!peutSuspendre || u.id === null) return null;
        const actif = u.statut === StatutMembre.ACTIF;
        return (
          <Bouton
            petit
            variante="discret"
            occupe={statutUtilisateur.isPending}
            onClick={() => {
              const motif = window.prompt(
                actif
                  ? `Motif de la désactivation du compte ${u.email} :`
                  : `Motif de la réactivation du compte ${u.email} :`,
              );
              if (motif === null || motif.trim().length < 3) return;
              statutUtilisateur.mutate(
                { id: u.id as string, actif: !actif, motif: motif.trim() },
                {
                  onSuccess: () => {
                    notifications.succes(
                      actif ? `Compte ${u.email} désactivé.` : `Compte ${u.email} réactivé.`,
                    );
                  },
                  onError: (erreur) => {
                    notifications.erreur(texteErreur(erreur));
                  },
                },
              );
            }}
          >
            {actif ? 'Désactiver' : 'Réactiver'}
          </Bouton>
        );
      },
    },
  ];

  const colonnesAppareils: Array<Colonne<AppareilExploitation>> = [
    { cle: 'nom', entete: 'Nom', rendu: (a) => a.nom },
    {
      cle: 'sn',
      entete: 'N° de série',
      rendu: (a) => <span className="gv-mono">{a.numero_serie_electrificateur}</span>,
    },
    { cle: 'modele', entete: 'Modèle', rendu: (a) => LIBELLE_MODELE[a.modele] },
    { cle: 'etat', entete: 'État', rendu: (a) => <BadgeEtatAppareil etat={a.statut_metier} /> },
    { cle: 'lien', entete: 'Liaison', rendu: (a) => <BadgeConnectivite etat={a.connectivite} /> },
    { cle: 'firmware', entete: 'Firmware', rendu: (a) => ouAbsent(a.firmware_version) },
    {
      cle: 'alertes',
      entete: 'Alertes',
      rendu: (a) => <PastilleNombre valeur={a.alertes_actives} />,
      numerique: true,
    },
    {
      cle: 'message',
      entete: 'Dernier message',
      rendu: (a) =>
        a.dernier_message_at ? formaterRelatif(a.dernier_message_at) : 'jamais vu',
    },
  ];

  return (
    <>
      <header className="gv-entete">
        <div className="gv-pile gv-pile--serree">
          <h1 className="gv-h1">{data.nom}</h1>
          <div className="gv-ligne">
            <BadgeStatutExploitation statut={data.statut} />
            <span className="gv-corps-s gv-secondaire">
              Créée le {formaterDateHeure(data.created_at)} · {data.fuseau_horaire_defaut}
            </span>
          </div>
        </div>
        <div className="gv-entete__actions">
          <Bouton
            onClick={() => {
              naviguer(`/audit?exploitation_id=${data.id}`);
            }}
          >
            Voir le journal d’audit
          </Bouton>
          {peutSuspendre ? (
            <Bouton
              variante={suspendue ? 'primaire' : 'danger'}
              onClick={() => {
                setSuspensionOuverte(true);
              }}
            >
              {suspendue ? 'Réactiver l’exploitation' : 'Suspendre l’exploitation'}
            </Bouton>
          ) : null}
        </div>
      </header>

      {suspendue ? (
        <div className="gv-bandeau gv-bandeau--attention">
          <div className="gv-pile gv-pile--serree">
            <strong>
              Exploitation suspendue le {formaterDateHeure(data.suspendue_at)} — les comptes ne
              peuvent plus se connecter.
            </strong>
            <span>Motif : {ouAbsent(data.motif_suspension)}</span>
          </div>
        </div>
      ) : null}

      <div className="gv-grille gv-grille--4">
        <Tuile libelle="Appareils" valeur={formaterNombre(data.appareils.length)} />
        <Tuile
          libelle="Utilisateurs"
          valeur={formaterNombre(
            data.utilisateurs.filter((u) => u.statut !== StatutMembre.INVITATION_EN_ATTENTE).length,
          )}
          note={`${formaterNombre(data.nb_invitations_en_attente)} invitation(s) en attente`}
        />
        <Tuile
          libelle="Alertes actives"
          valeur={formaterNombre(data.nb_alertes_actives)}
          ton={data.nb_alertes_actives > 0 ? 'orange' : 'vert'}
        />
        <Tuile
          libelle="Administrateur"
          valeur={
            <span style={{ fontSize: 18 }}>
              {data.administrateur ? `${data.administrateur.prenom} ${data.administrateur.nom}` : VALEUR_ABSENTE}
            </span>
          }
          note={data.administrateur?.email ?? ''}
        />
      </div>

      <Carte titre="Identité">
        <Definitions
          entrees={[
            { cle: 'Identifiant', valeur: <span className="gv-mono gv-corps-s">{data.id}</span> },
            { cle: 'Fuseau horaire par défaut', valeur: data.fuseau_horaire_defaut },
            { cle: 'Création', valeur: formaterDateHeure(data.created_at) },
            { cle: 'Suspension', valeur: formaterDateHeure(data.suspendue_at) },
          ]}
        />
      </Carte>

      <Carte titre="Utilisateurs" sousTitre="Comptes et invitations en attente" sansPadding>
        <Tableau
          colonnes={colonnesUtilisateurs}
          lignes={data.utilisateurs}
          cleDe={(u) => u.id ?? u.email}
          titreVide="Aucun utilisateur"
        />
      </Carte>

      <Carte titre="Appareils" sousTitre="Électrificateurs appairés à cette exploitation" sansPadding>
        <Tableau
          colonnes={colonnesAppareils}
          lignes={data.appareils}
          cleDe={(a) => a.id}
          titreVide="Aucun appareil"
          descriptionVide="Cette exploitation n’a appairé aucun électrificateur."
          surClic={(a) => {
            naviguer(`/appareils/${encodeURIComponent(a.numero_serie_electrificateur)}`);
          }}
        />
      </Carte>

      <p className="gv-corps-s gv-secondaire">
        Besoin de la fiche d’un appareil précis ? <Link to="/stock">Rechercher dans le stock</Link>.
      </p>

      {suspensionOuverte ? (
        <ModaleSuspension
          id={data.id}
          nom={data.nom}
          suspendue={suspendue}
          surFermeture={() => {
            setSuspensionOuverte(false);
          }}
        />
      ) : null}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Suspension / réactivation                                                   */
/* -------------------------------------------------------------------------- */

function ModaleSuspension({
  id,
  nom,
  suspendue,
  surFermeture,
}: {
  id: string;
  nom: string;
  suspendue: boolean;
  surFermeture: () => void;
}): JSX.Element {
  const notifications = useNotifications();
  const mutation = useChangerStatutExploitation(id);
  const [motif, setMotif] = useState('');
  const [confirmation, setConfirmation] = useState('');

  // Confirmation par recopie du nom : une suspension coupe l'accès de TOUS les comptes d'une
  // exploitation, y compris pendant une nuit de surveillance. Le geste doit être délibéré.
  const valide = motif.trim().length >= 3 && (suspendue || confirmation.trim() === nom);

  return (
    <Modale
      titre={suspendue ? `Réactiver ${nom}` : `Suspendre ${nom}`}
      ouverte
      surFermeture={surFermeture}
      pied={
        <>
          <Bouton variante="discret" onClick={surFermeture}>
            Annuler
          </Bouton>
          <Bouton
            variante={suspendue ? 'primaire' : 'danger'}
            disabled={!valide}
            occupe={mutation.isPending}
            onClick={() => {
              mutation.mutate(
                {
                  statut: suspendue ? StatutExploitation.ACTIVE : StatutExploitation.SUSPENDUE,
                  motif: motif.trim(),
                },
                {
                  onSuccess: (reponse) => {
                    notifications.succes(
                      suspendue
                        ? `${nom} réactivée.`
                        : `${nom} suspendue — ${reponse.sessions_revoquees} session(s) révoquée(s).`,
                    );
                    surFermeture();
                  },
                  onError: (erreur) => {
                    notifications.erreur(texteErreur(erreur));
                  },
                },
              );
            }}
          >
            {suspendue ? 'Réactiver' : 'Suspendre'}
          </Bouton>
        </>
      }
    >
      {!suspendue ? (
        <div className="gv-bandeau gv-bandeau--erreur">
          <div className="gv-pile gv-pile--serree">
            <strong>Ce que fait la suspension</strong>
            <span>
              Les comptes de l’exploitation ne peuvent plus se connecter et leurs sessions en cours
              sont révoquées ; les notifications sortantes sont interrompues. L’ingestion des trames
              CONTINUE : l’historique reste complet et un vol survenu pendant la suspension reste
              tracé.
            </span>
          </div>
        </div>
      ) : null}

      <ZoneTexte
        label="Motif"
        aide="Obligatoire, consigné au journal d’audit. 200 caractères maximum."
        maxLength={200}
        value={motif}
        onChange={(e) => {
          setMotif(e.currentTarget.value);
        }}
      />

      {!suspendue ? (
        <label className="gv-champ">
          <span className="gv-champ__label">Recopiez le nom de l’exploitation pour confirmer</span>
          <input
            className="gv-saisie"
            value={confirmation}
            placeholder={nom}
            onChange={(e) => {
              setConfirmation(e.currentTarget.value);
            }}
          />
        </label>
      ) : null}

      {mutation.error ? <MessageErreur erreur={mutation.error} /> : null}
    </Modale>
  );
}
